import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  BookOpen,
  Compass,
  Settings as SettingsIcon,
} from 'lucide-react';
import { ChatBubble, type ChatMessage } from '../components/ChatBubble';
import { ChatInput, type ChatInputSubmission } from '../components/ChatInput';
import { OnboardingDateStep } from '../components/DateInputs';
import { OnboardingTextStep } from '../components/OnboardingTextStep';
import { QuickReplies } from '../components/QuickReplies';
import { StarterChips } from '../components/StarterChips';
import { LanguageSelector } from '../components/LanguageSelector';
import { InstallNudge } from '../components/InstallNudge';
import {
  inboxQueryKey,
  unreadCountQueryKey,
  useUnreadCount,
} from '../lib/api/announcements';
import { useSendMessage } from '../lib/api/messages';
import {
  chatHistoryQueryKey,
  reconcileHistory,
  useChatHistory,
} from '../lib/api/chatHistory';
import {
  getTodayPicks,
  teatimePicksQueryKey,
  type TeatimePicksResponse,
} from '../lib/api/picks';
import { usePrefetchEssentials } from '../lib/api/prefetch';
import { usePartner, useUpdateProfile, useUser } from '../lib/api/useUser';
import { useFaqs } from '../lib/api/faqs';
import { useConnection } from '../lib/connection';
import { matchFaq } from '../lib/offlineFaqMatcher';
import {
  type AttachmentDraft,
  type AttachmentRecord,
  revokeDraft,
  uploadAttachment,
} from '../lib/media';
import {
  buildProfile,
  formatSelection,
  isNameSelection,
  isPartyWithDogSelection,
  isStayDatesSelection,
  isoDateLocal,
  ONBOARDING_STEPS,
  type OnboardingSelection,
  type Party,
} from '../lib/onboarding';
import {
  computeNextRetryAt,
  enqueueOutboxItem,
  readOutbox,
  removeOutboxItem,
  updateOutboxItem,
  type OutboxItem,
} from '../lib/outbox';
import { resolvePersona } from '../lib/personas';
import {
  readChatHistory,
  readMemoryOffered,
  readStartersDismissed,
  setMemoryOffered,
  setStartersDismissed,
  writeChatHistory,
} from '../lib/storage';
import { isPastStayCutoff } from '../lib/stayCutoff';
import { getLocalDateString, useTeatimeNudge } from '../lib/teatime';

type OnboardingStepIndex = number | 'done';
type SupportedLanguage = 'en' | 'fr' | 'de' | 'es';

const PERMANENT_ERROR_CODES = new Set<string>([
  'validation_error',
  'unauthorized',
  'forbidden',
  'not_found',
  'stay_ended',
]);

function classifyError(code: string): 'failed-retryable' | 'failed-permanent' {
  return PERMANENT_ERROR_CODES.has(code)
    ? 'failed-permanent'
    : 'failed-retryable';
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeNow(): string {
  return formatTime(new Date());
}

function draftToRecord(draft: AttachmentDraft): AttachmentRecord {
  const record: AttachmentRecord = {
    id: draft.id,
    kind: draft.kind,
    url: draft.previewUrl,
    mime: draft.mime,
    sizeBytes: draft.sizeBytes,
  };
  if (draft.width !== undefined) record.width = draft.width;
  if (draft.height !== undefined) record.height = draft.height;
  if (draft.durationMs !== undefined) record.durationMs = draft.durationMs;
  return record;
}

function outboxItemToBubble(item: OutboxItem): ChatMessage {
  return {
    id: `u-${item.id}`,
    from: 'user',
    text: item.text,
    timestamp: formatTime(new Date(item.createdAt)),
    dateLocal: item.dateLocal,
    kind: 'user',
    status: 'sent',
    outboxStatus: item.status,
    outboxItemId: item.id,
  };
}

function resolveLanguage(value: string | undefined): SupportedLanguage {
  if (value === 'fr' || value === 'de' || value === 'es') return value;
  return 'en';
}

export default function MePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getToken, userId } = useAuth();
  const { t, i18n } = useTranslation('mePage');
  // Second namespace — the bell icon's aria-label keys live in the
  // `inbox` namespace (Phase 5c) rather than polluting mePage.json
  // with cross-feature keys. Using a separate useTranslation call
  // keeps the namespace boundary explicit at the call site.
  const { t: tInbox } = useTranslation('inbox');

  const { data: user, isLoading, isError } = useUser();
  const { data: partner } = usePartner();
  const updateProfileMutation = useUpdateProfile();

  // Unread announcements count for the bell badge (Phase 5c
  // sub-commit 5). Gated on signed-in by the hook itself.
  // refetchOnWindowFocus + refetchOnMount + the SW message-driven
  // invalidation below keep this fresh across the open-app and
  // returning-from-other-route flows.
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  // Phase 6 Offline-First sub-commit 2 (13 Jun 2026) — eagerly populate
  // inbox + today's picks caches once per day per user. Fire-and-forget,
  // gated on signed-in + onboarding complete + online. The SW (sub-commit 1)
  // captures the responses in `ti-api-v5` for offline reads. See
  // src/lib/api/prefetch.ts.
  usePrefetchEssentials();

  // Phase 6 Offline-First sub-commit 6b (13 Jun 2026) — FAQ catalogue
  // for the offline AI fallback inside handleSend. Cached by SW + the
  // once-per-day prefetch above; 1h staleTime in-memory. data is
  // undefined while loading, and `data.faqs` is `[]` for unattributed
  // guests (server returns empty rather than 404 on the no-partner
  // case). Both states result in matchFaq returning null, which falls
  // through cleanly to the normal outbox queue path.
  const { data: faqsData } = useFaqs();

  const chatHistoryQuery = useChatHistory({ enabled: !!user?.profile });
  const chatHistoryData = chatHistoryQuery.data;

  const persona = useMemo(
    () => (user ? resolvePersona(user.townSlug, partner ?? null) : null),
    [user, partner],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [outboxItems, setOutboxItems] = useState<OutboxItem[]>([]);
  const refreshOutbox = useCallback(() => {
    setOutboxItems(userId ? readOutbox(userId) : []);
  }, [userId]);
  // Periodic tick that re-runs the drain effect every 5s while
  // shouldSend is true. Backoff (sub-commit 4b) stamps each failed-
  // retryable item with a future nextRetryAt; this tick is the
  // mechanism that picks them up when the backoff window elapses.
  // Bumping a state value is the cleanest way to invalidate the
  // drain effect's memoised dep array — a ref alone wouldn't
  // trigger the re-render React needs to re-run the effect.
  const [drainTick, setDrainTick] = useState(0);
  const prevShouldSendRef = useRef<boolean>(true);
  const [step, setStep] = useState<OnboardingStepIndex>('done');
  const [starterShown, setStarterShown] = useState<boolean>(
    () => !readStartersDismissed(),
  );
  const [chatClosedByServer, setChatClosedByServer] = useState(false);

  const partialProfileRef = useRef<{
    stayStart?: string;
    stayEnd?: string;
    party?: Party;
    hasDog?: boolean;
    name?: string;
  }>({});
  const scrollRef = useRef<HTMLElement | null>(null);
  const ownedDraftsRef = useRef<AttachmentDraft[]>([]);

  const {
    send: sendChat,
    isStreaming,
    abort: abortChat,
  } = useSendMessage();

  const { shouldSend } = useConnection();

  useEffect(() => {
    if (isLoading) return;
    if (isError || !user || !persona) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isError, user, persona, navigate]);

  useEffect(() => {
    if (!persona || !user) return;
    if (messages.length > 0) return;
    if (user.profile) {
      const rawHistory = readChatHistory(userId) ?? [];
      const pending = userId ? readOutbox(userId) : [];
      const pendingBubbles = pending.map(outboxItemToBubble);
      setMessages([...rawHistory, ...pendingBubbles]);
      setOutboxItems(pending);
      setStep('done');
      return;
    }
    const welcomeMsg: ChatMessage = {
      id: 'onboard-welcome',
      from: 'persona',
      text: persona.onboarding.welcomeIntro,
      timestamp: timeNow(),
      dateLocal: getLocalDateString(),
      kind: 'onboarding',
      status: 'sent',
    };
    setMessages([welcomeMsg]);
    setStep(0);
    const firstStep = ONBOARDING_STEPS[0];
    if (!firstStep) return;
    const promptTimer = setTimeout(() => {
      const promptMsg: ChatMessage = {
        id: `onboard-prompt-${firstStep.id}`,
        from: 'persona',
        text: persona.onboarding.prompts[firstStep.id],
        timestamp: timeNow(),
        dateLocal: getLocalDateString(),
        kind: 'onboarding',
        status: 'sent',
      };
      setMessages((prev) => [...prev, promptMsg]);
    }, 600);
    return () => clearTimeout(promptTimer);
  }, [persona, user, userId, messages.length]);

  useEffect(() => {
    refreshOutbox();
  }, [refreshOutbox]);

  useEffect(() => {
    if (messages.length === 0) return;
    if (isStreaming) return;
    const stableMessages = messages.filter(
      (m) => m.outboxStatus === undefined,
    );
    writeChatHistory(stableMessages, userId);
  }, [messages, isStreaming, userId]);

  useEffect(() => {
    if (!chatHistoryData) return;
    if (!user?.profile) return;
    if (isStreaming) return;
    setMessages((prev) => {
      const stable = prev.filter((m) => m.outboxStatus === undefined);
      const pending = prev.filter((m) => m.outboxStatus !== undefined);
      const reconciled = reconcileHistory(stable, chatHistoryData);
      return [...reconciled, ...pending];
    });
  }, [chatHistoryData, user, isStreaming]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      ownedDraftsRef.current.forEach(revokeDraft);
    };
  }, []);

  useEffect(() => {
    return () => {
      abortChat();
    };
  }, [abortChat]);

  /**
   * Service Worker push-message listener (Phase 5c sub-commit 5).
   *
   * The SW posts `{ type: 'PUSH_RECEIVED', tag }` to all clients on
   * every push event (see public/sw.js v4). When the tag starts
   * with `announcement:` (the prefix used by partner announcements
   * server-side), we invalidate the two announcement queries —
   * inbox list + unread-count — so the bell badge and inbox view
   * reflect server state instantly without waiting for the next
   * focus/mount/stale-time refetch.
   *
   * Identity-scoped invalidation: pass the same userId the hooks
   * read with. Generic prefix matching (`['announcements']`) would
   * work but invalidates entries for any cached user — wasteful and
   * inconsistent with the rest of the codebase's scoping discipline.
   *
   * Graceful degradation: no-op in browsers without serviceWorker
   * support (older mobile browsers, jsdom in tests).
   */
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'PUSH_RECEIVED') return;
      const tag = typeof data.tag === 'string' ? data.tag : '';
      if (!tag.startsWith('announcement:')) return;
      void queryClient.invalidateQueries({
        queryKey: inboxQueryKey(userId ?? null),
      });
      void queryClient.invalidateQueries({
        queryKey: unreadCountQueryKey(userId ?? null),
      });
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [queryClient, userId]);

  /**
   * PWA app icon badge (Phase 5c sub-commit 5).
   *
   * Mirrors the unread count onto the OS-level app icon badge via
   * the App Badging API. Supported in modern Chrome (desktop +
   * Android), Edge, and iOS 16.4+ Safari when installed as a PWA.
   * Older browsers and jsdom (tests) early-return silently — the
   * badge is decorative, never load-bearing.
   *
   * Try/catch around the call protects against runtime permissions
   * errors (e.g. a browser that exposes the API but rejects the
   * call in non-PWA contexts).
   */
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!('setAppBadge' in navigator)) return;
    const nav = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    try {
      if (unreadCount > 0) {
        void nav.setAppBadge?.(unreadCount);
      } else {
        void nav.clearAppBadge?.();
      }
    } catch {
      // Silent — badge is decorative; never block on its failure.
    }
  }, [unreadCount]);

  useEffect(() => {
    const wasOffline = !prevShouldSendRef.current;
    const isOnlineNow = shouldSend;
    prevShouldSendRef.current = shouldSend;
    if (!wasOffline || !isOnlineNow) return;
    if (!userId) return;
    const items = readOutbox(userId);
    const retryable = items.filter((i) => i.status === 'failed-retryable');
    if (retryable.length === 0) return;
    for (const item of retryable) {
      updateOutboxItem(userId, item.id, {
        status: 'queued',
        lastError: null,
        // Network recovery = strongest signal. Clear any pending
        // backoff so the drain effect picks the item up immediately
        // instead of waiting for its scheduled nextRetryAt.
        nextRetryAt: null,
      });
    }
    refreshOutbox();
    setMessages((prev) =>
      prev.map((m) =>
        m.outboxStatus === 'failed-retryable'
          ? { ...m, outboxStatus: 'queued' as const }
          : m,
      ),
    );
  }, [shouldSend, userId, refreshOutbox]);

  const handleTeatimeFire = useCallback(async () => {
    if (!persona || !user) return;
    const todayLocal = getLocalDateString();
    const joinedDateLocal = isoDateLocal(new Date(user.joinedAt));
    if (todayLocal === joinedDateLocal) return;
    const date = todayLocal;
    try {
      const result = await queryClient.fetchQuery<TeatimePicksResponse>({
        queryKey: teatimePicksQueryKey(persona.slug, date),
        queryFn: () => getTodayPicks({ date, getToken }),
        staleTime: 4 * 60 * 60 * 1000,
      });
      const msg: ChatMessage = {
        id: `teatime-${date}-${persona.slug}`,
        from: 'persona',
        text: persona.nudges.teatime.format(result.picks),
        timestamp: timeNow(),
        dateLocal: date,
        kind: 'teatime',
        status: 'sent',
      };
      setMessages((prev) => [...prev, msg]);
    } catch {
      const errMsg: ChatMessage = {
        id: `teatime-err-${date}-${persona.slug}`,
        from: 'persona',
        text: persona.nudges.teatime.errorReply,
        timestamp: timeNow(),
        dateLocal: date,
        kind: 'teatime',
        status: 'sent',
      };
      setMessages((prev) => [...prev, errMsg]);
    }
  }, [persona, user, queryClient, getToken]);

  const teatimeConfig = useMemo(() => {
    const base = {
      enabled: step === 'done' && !!user?.profile,
      onFire: handleTeatimeFire,
    };
    return {
      ...base,
      ...(user?.profile?.stayStart !== undefined
        ? { stayStart: user.profile.stayStart }
        : {}),
      ...(user?.profile?.stayEnd !== undefined
        ? { stayEnd: user.profile.stayEnd }
        : {}),
    };
  }, [step, user, handleTeatimeFire]);
  useTeatimeNudge(teatimeConfig);

  const memoryOfferFiredRef = useRef(false);
  useEffect(() => {
    if (!persona || !user?.profile || step !== 'done') return;
    if (memoryOfferFiredRef.current) return;
    const stayEnd = user.profile.stayEnd;
    const offered = readMemoryOffered();
    if (offered?.forStayEnd === stayEnd) {
      memoryOfferFiredRef.current = true;
      return;
    }
    const today = isoDateLocal(new Date());
    const stayEndDate = new Date(stayEnd);
    stayEndDate.setDate(stayEndDate.getDate() - 1);
    const oneDayBefore = isoDateLocal(stayEndDate);
    if (today !== stayEnd && today !== oneDayBefore) return;
    memoryOfferFiredRef.current = true;
    const msg: ChatMessage = {
      id: `memory-offer-${stayEnd}-${persona.slug}`,
      from: 'persona',
      text: persona.memoryPageSuggestion,
      timestamp: timeNow(),
      dateLocal: today,
      kind: 'memory-offer',
      status: 'sent',
    };
    setMessages((prev) => [...prev, msg]);
    setMemoryOffered({ forStayEnd: stayEnd });
  }, [persona, user, step]);

  const starters = useMemo(() => {
    if (!persona) return [];
    if (!user?.profile) return [];
    return persona.getStarters(user.profile);
  }, [persona, user]);

  const chatClosed = useMemo(() => {
    if (chatClosedByServer) return true;
    if (!user?.profile?.stayEnd) return false;
    return isPastStayCutoff(user.profile.stayEnd, getLocalDateString());
  }, [chatClosedByServer, user]);

  // Periodic tick to re-run the drain effect for backoff-due items
  // (sub-commit 4b). Only runs while shouldSend is true — when
  // offline, draining would just fail. 5s cadence balances
  // responsiveness against wakeup cost; backoff timers cap at 30s
  // so a 5s tick guarantees at most a 5s delay past the scheduled
  // retry. Cleared on shouldSend false or unmount.
  useEffect(() => {
    if (!shouldSend) return;
    const interval = setInterval(() => {
      setDrainTick((t) => t + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [shouldSend]);

  useEffect(() => {
    if (!shouldSend) return;
    if (isStreaming) return;
    if (!userId) return;
    // Drain "due retryable" items: queued OR failed-retryable, AND
    // nextRetryAt is null (no backoff) or in the past (backoff
    // elapsed). The drainTick interval above re-runs this filter
    // every 5s to catch items whose backoff windows have rolled
    // over since the last evaluation.
    const now = Date.now();
    const due = outboxItems.filter(
      (i) =>
        (i.status === 'queued' || i.status === 'failed-retryable') &&
        (i.nextRetryAt === null ||
          new Date(i.nextRetryAt).getTime() <= now),
    );
    if (due.length === 0) return;
    const item = due[0];
    if (!item) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.outboxItemId === item.id
          ? { ...m, outboxStatus: 'sending' as const }
          : m,
      ),
    );
    const placeholderId = `p-${Date.now()}-${item.id}`;
    const placeholder: ChatMessage = {
      id: placeholderId,
      from: 'persona',
      text: '',
      timestamp: timeNow(),
      dateLocal: item.dateLocal,
      kind: 'persona-reply',
      status: 'sending',
    };
    setMessages((prev) => [...prev, placeholder]);
    void sendChat(
      {
        text: item.text,
        dateLocal: item.dateLocal,
        clientNonce: item.clientNonce,
      },
      {
        onTextDelta: (delta) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? { ...m, text: m.text + delta }
                : m,
            ),
          );
        },
        onComplete: () => {
          removeOutboxItem(userId, item.id);
          refreshOutbox();
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id === placeholderId) {
                return { ...m, status: 'sent' as const };
              }
              if (m.outboxItemId === item.id) {
                const {
                  outboxStatus: _os,
                  outboxItemId: _oi,
                  ...rest
                } = m;
                return rest;
              }
              return m;
            }),
          );
          // Identity-scoped invalidation (Batch 2 Part A) — pass the
          // same userId the read hooks use, so the cache entry we
          // wrote to is the one we ask TanStack to refetch.
          queryClient.invalidateQueries({
            queryKey: chatHistoryQueryKey(userId),
          });
        },
        onError: (event) => {
          const classified = classifyError(event.code);
          const newAttemptCount = item.attemptCount + 1;
          updateOutboxItem(userId, item.id, {
            status: classified,
            attemptCount: newAttemptCount,
            lastError: event.code,
            // Stamp backoff only for retryable failures. Permanent
            // failures never auto-retry, so a next-retry time is
            // meaningless for them.
            nextRetryAt:
              classified === 'failed-retryable'
                ? computeNextRetryAt(newAttemptCount)
                : null,
          });
          refreshOutbox();
          if (event.code === 'stay_ended') {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === placeholderId) {
                  return {
                    ...m,
                    text: event.message,
                    status: 'sent' as const,
                  };
                }
                if (m.outboxItemId === item.id) {
                  return { ...m, outboxStatus: classified };
                }
                return m;
              }),
            );
            setChatClosedByServer(true);
            return;
          }
          setMessages((prev) =>
            prev
              .filter((m) => m.id !== placeholderId)
              .map((m) =>
                m.outboxItemId === item.id
                  ? { ...m, outboxStatus: classified }
                  : m,
              ),
          );
        },
      },
    );
  }, [
    shouldSend,
    isStreaming,
    userId,
    outboxItems,
    drainTick,
    sendChat,
    queryClient,
    refreshOutbox,
  ]);

  const handleRetry = useCallback(
    (outboxItemId: string) => {
      if (!userId) return;
      updateOutboxItem(userId, outboxItemId, {
        status: 'queued',
        lastError: null,
        // Manual retry button = explicit user intent; bypass any
        // pending backoff timer.
        nextRetryAt: null,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.outboxItemId === outboxItemId
            ? { ...m, outboxStatus: 'queued' as const }
            : m,
        ),
      );
      refreshOutbox();
    },
    [userId, refreshOutbox],
  );

  const handleDismiss = useCallback(
    (outboxItemId: string) => {
      if (!userId) return;
      removeOutboxItem(userId, outboxItemId);
      setMessages((prev) =>
        prev.filter((m) => m.outboxItemId !== outboxItemId),
      );
      refreshOutbox();
    },
    [userId, refreshOutbox],
  );

  if (isLoading) return null;
  if (!user || !persona) return null;

  const partnerPrimary = partner?.theme?.primaryHex;
  const partnerName = partner?.name;

  // Bell aria-label switches plural form via i18next's _one/_other
  // suffix when `count` is in the interpolation values. Count of 0
  // falls back to the plain bellAriaLabel; >= 1 uses the plural-aware
  // bellAriaLabelWithUnread keys.
  const bellAriaLabel =
    unreadCount > 0
      ? tInbox('bellAriaLabelWithUnread', { count: unreadCount })
      : tInbox('bellAriaLabel');

  const updateMessage = (id: string, patch: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const runUploads = (messageId: string, drafts: AttachmentDraft[]) => {
    const totals = drafts.map((d) => d.sizeBytes);
    const grandTotal = totals.reduce((a, b) => a + b, 0);
    const loaded: number[] = drafts.map(() => 0);
    const handles = drafts.map((draft, idx) =>
      uploadAttachment(draft, ({ loaded: l }) => {
        loaded[idx] = l;
        const sum = loaded.reduce((a, b) => a + b, 0);
        const pct = grandTotal > 0 ? Math.round((sum / grandTotal) * 100) : 100;
        updateMessage(messageId, { uploadProgress: pct });
      }),
    );
    Promise.all(handles.map((h) => h.promise))
      .then(() => updateMessage(messageId, { status: 'sent', uploadProgress: 100 }))
      .catch(() => updateMessage(messageId, { status: 'error' }));
  };

  const handleSend = ({ text, attachments }: ChatInputSubmission) => {
    if (isStreaming) return;
    if (chatClosed) return;
    if (starterShown) {
      setStarterShown(false);
      setStartersDismissed();
    }
    const records = attachments.map(draftToRecord);
    ownedDraftsRef.current.push(...attachments);
    if (text.trim().length === 0) {
      const userMsgId = `u-${Date.now()}`;
      const baseMsg: ChatMessage = {
        id: userMsgId,
        from: 'user',
        text,
        timestamp: timeNow(),
        dateLocal: getLocalDateString(),
        kind: 'user',
        status: attachments.length > 0 ? 'sending' : 'sent',
      };
      const userMsg: ChatMessage = {
        ...baseMsg,
        ...(records.length > 0 ? { attachments: records } : {}),
        ...(attachments.length > 0 ? { uploadProgress: 0 } : {}),
      };
      setMessages((prev) => [...prev, userMsg]);
      if (attachments.length > 0) {
        runUploads(userMsgId, attachments);
      }
      return;
    }
    if (!userId) return;
    // Phase 6 sub-commit 6b (13 Jun 2026) — offline FAQ fallback.
    // When shouldSend is false AND the guest's query matches an FAQ
    // in the cached catalogue above the matcher's confidence threshold,
    // surface the saved answer inline as a `persona-offline-faq` bubble
    // and skip the outbox entirely. The FAQ IS the answer; queueing the
    // message would produce a double-answer when the user returns
    // online (cached FAQ already in chat + live persona reply on
    // drain). Premium discipline: one answer per question, distinguished
    // visually as cached.
    //
    // Gated on text-only sends (attachments.length === 0). Attachment-
    // bearing messages always route through the outbox so attachments
    // aren't dropped — the offline matcher reads text only.
    //
    // Reconciliation behaviour: the synthetic user message uses kind
    // 'user' which is in SERVER_OWNED_CLIENT_KINDS, but reconcileHistory
    // (lib/api/chatHistory.ts) falls through to "no match — keep client
    // version" when no server row matches (none ever will — this user
    // message is never sent). The persona-offline-faq bubble uses a
    // client-only kind not in SERVER_OWNED_CLIENT_KINDS, so passes
    // through unchanged. Both survive reload + server sync within the
    // 7-day history retention window.
    if (!shouldSend && attachments.length === 0 && faqsData) {
      const match = matchFaq(text, faqsData.faqs);
      if (match) {
        const dateLocal = getLocalDateString();
        const now = Date.now();
        const userMsg: ChatMessage = {
          id: `u-faq-${now}`,
          from: 'user',
          text,
          timestamp: timeNow(),
          dateLocal,
          kind: 'user',
          status: 'sent',
        };
        const faqMsg: ChatMessage = {
          id: `pofq-${now}`,
          from: 'persona',
          text: match.faq.answer,
          timestamp: timeNow(),
          dateLocal,
          kind: 'persona-offline-faq',
          status: 'sent',
        };
        setMessages((prev) => [...prev, userMsg, faqMsg]);
        return;
      }
    }
    const language = resolveLanguage(i18n.resolvedLanguage);
    const item = enqueueOutboxItem(userId, {
      text,
      dateLocal: getLocalDateString(),
      language,
    });
    const userMsg: ChatMessage = {
      id: `u-${item.id}`,
      from: 'user',
      text,
      timestamp: timeNow(),
      dateLocal: item.dateLocal,
      kind: 'user',
      status: 'sent',
      outboxStatus: 'queued',
      outboxItemId: item.id,
      ...(records.length > 0 ? { attachments: records } : {}),
    };
    setMessages((prev) => [...prev, userMsg]);
    refreshOutbox();
    if (attachments.length > 0) {
      runUploads(userMsg.id, attachments);
    }
  };

  const handleStarterPick = (prompt: string) => {
    handleSend({ text: prompt, attachments: [] });
  };

  const handleQuickReply = async (selection: OnboardingSelection) => {
    if (step === 'done') return;
    const stepConfig = ONBOARDING_STEPS[step];
    if (!stepConfig) return;
    const replyText = formatSelection(stepConfig, selection);
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      from: 'user',
      text: replyText,
      timestamp: timeNow(),
      dateLocal: getLocalDateString(),
      kind: 'user',
      status: 'sent',
    };
    setMessages((prev) => [...prev, userMsg]);
    if (stepConfig.id === 'name') {
      if (isNameSelection(selection) && selection.name !== undefined) {
        partialProfileRef.current.name = selection.name;
      }
    } else if (stepConfig.id === 'stay-dates') {
      if (isStayDatesSelection(selection)) {
        partialProfileRef.current.stayStart = selection.stayStart;
        partialProfileRef.current.stayEnd = selection.stayEnd;
      }
    } else if (stepConfig.id === 'party-with-dog') {
      if (isPartyWithDogSelection(selection)) {
        partialProfileRef.current.party = selection.party;
        partialProfileRef.current.hasDog = selection.hasDog;
      }
    }
    const nextIdx = step + 1;
    if (nextIdx < ONBOARDING_STEPS.length) {
      const nextStepConfig = ONBOARDING_STEPS[nextIdx];
      if (!nextStepConfig) return;
      setTimeout(() => {
        const promptMsg: ChatMessage = {
          id: `onboard-prompt-${nextStepConfig.id}`,
          from: 'persona',
          text: persona.onboarding.prompts[nextStepConfig.id],
          timestamp: timeNow(),
          dateLocal: getLocalDateString(),
          kind: 'onboarding',
          status: 'sent',
        };
        setMessages((prev) => [...prev, promptMsg]);
        setStep(nextIdx);
      }, 400);
      return;
    }
    const { stayStart, stayEnd, party, hasDog, name } =
      partialProfileRef.current;
    if (
      stayStart === undefined ||
      stayEnd === undefined ||
      party === undefined ||
      hasDog === undefined
    ) {
      return;
    }
    const profile = buildProfile({
      stayStart,
      stayEnd,
      party,
      hasDog,
      ...(name !== undefined ? { name } : {}),
    });
    try {
      await updateProfileMutation.mutateAsync(profile);
    } catch {
      const errMsg: ChatMessage = {
        id: `onboard-err-${Date.now()}`,
        from: 'persona',
        text: persona.errorReply,
        timestamp: timeNow(),
        dateLocal: getLocalDateString(),
        kind: 'onboarding',
        status: 'sent',
      };
      setMessages((prev) => [...prev, errMsg]);
      return;
    }
    setTimeout(() => {
      const ackMsg: ChatMessage = {
        id: `onboard-ack-${Date.now()}`,
        from: 'persona',
        text: persona.onboarding.buildAck(profile),
        timestamp: timeNow(),
        dateLocal: getLocalDateString(),
        kind: 'onboarding',
        status: 'sent',
      };
      setMessages((prev) => [...prev, ackMsg]);
      setStep('done');
      setTimeout(() => {
        const welcomeMsg: ChatMessage = {
          id: `onboard-welcome-after-${Date.now()}`,
          from: 'persona',
          text: persona.onboarding.welcomeAfterOnboarding,
          timestamp: timeNow(),
          dateLocal: getLocalDateString(),
          kind: 'onboarding',
          status: 'sent',
        };
        setMessages((prev) => [...prev, welcomeMsg]);
      }, 700);
    }, 400);
  };

  const currentStepConfig =
    step === 'done' ? undefined : ONBOARDING_STEPS[step];

  return (
    <>
      <Helmet>
        {/*
          Tab title — partner-aware (14 Jun 2026). Mirrors the header
          pill fallback: when partner-attributed, the tab shows the
          partner's business name (so each partner gets their own tab);
          otherwise falls back to the bare-town brand string
          ("Padstow Insider") via the `townInsider` i18n key. The brand
          suffix is preserved across languages — see common.json.
        */}
        <title>{partnerName ?? t('townInsider', { townLabel: persona.townLabel })}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div
        className={`fixed inset-0 flex flex-col ${
          partnerPrimary ? 'bg-white' : 'bg-cream-50'
        }`}
      >
        <header
          className={`border-b border-moor-100 backdrop-blur ${
            partnerPrimary ? 'bg-white/95' : 'bg-cream-50/95'
          }`}
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="container-narrow flex items-center gap-3 px-4 py-3">
            <div
              aria-hidden="true"
              className={`flex h-10 w-10 items-center justify-center rounded-full text-cream-50 ${
                partnerPrimary ? '' : 'bg-moor-700'
              }`}
              style={
                partnerPrimary
                  ? { backgroundColor: partnerPrimary }
                  : undefined
              }
            >
              <Compass className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs uppercase tracking-wide text-moor-500">
                {partnerName ?? t('townInsider', { townLabel: persona.townLabel })}
              </p>
              <h1 className="truncate text-base font-semibold text-moor-900">
                {persona.personaName}
              </h1>
            </div>
            <LanguageSelector />
            {/*
              Inbox bell (Phase 5c sub-commit 5). Highest-frequency
              icon → first position among the header icon links.
              Badge ring color adapts to the header background so the
              badge visually "pops" cleanly on both partner-themed
              (white) and town-default (cream) chrome. Badge value
              caps at "99+" to prevent layout overflow on long counts.
            */}
            <Link
              to="/inbox"
              aria-label={bellAriaLabel}
              className="relative flex h-9 w-9 items-center justify-center rounded-full text-moor-600 transition-colors hover:bg-moor-50 hover:text-moor-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <Bell className="h-5 w-5" aria-hidden="true" />
              {unreadCount > 0 ? (
                <span
                  aria-hidden="true"
                  className={`absolute -top-0.5 -right-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-moor-700 px-1 text-[10px] font-semibold text-cream-50 ring-2 ${
                    partnerPrimary ? 'ring-white' : 'ring-cream-50'
                  }`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </Link>
            <Link
              to="/settings"
              aria-label={t('settingsAriaLabel')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-moor-600 transition-colors hover:bg-moor-50 hover:text-moor-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <SettingsIcon className="h-5 w-5" aria-hidden="true" />
            </Link>
            <Link
              to={`/memories/${user.townSlug}`}
              aria-label={t('memoryAriaLabel')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-moor-600 transition-colors hover:bg-moor-50 hover:text-moor-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <BookOpen className="h-5 w-5" aria-hidden="true" />
            </Link>
            {/*
              Home text link. Hidden on mobile via `hidden sm:inline-block`
              (Phase 5c polish 1, 13 Jun 2026). The link redirects to /
              which immediately bounces signed-in users back to /me — it's
              functionally a no-op from this page, and on narrow viewports
              it pushes the rest of the chrome into a "Ho..." truncation.
              On desktop (sm: 640px+) it remains visible as a familiar
              top-right home affordance. Test suite still finds it via
              getByText since CSS media queries don't apply in jsdom.
            */}
            <Link
              to="/"
              className="hidden text-sm text-moor-600 underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-moor-500/40 focus:ring-offset-2 sm:inline-block"
            >
              {t('homeLink')}
            </Link>
          </div>
        </header>
        <main
          ref={scrollRef}
          id="main-content"
          className="min-h-0 flex-1 overflow-y-auto"
          aria-live="polite"
        >
          <div className="container-narrow flex min-h-full flex-col gap-3 px-4 py-4">
            <div className="flex-1" aria-hidden="true" />
            {messages.map((m) => (
              <ChatBubble
                key={m.id}
                message={m}
                partnerPrimary={partnerPrimary}
                onRetry={handleRetry}
                onDismiss={handleDismiss}
              />
            ))}
          </div>
        </main>
        {/*
          Phase 6 sub-commit 7 (13 Jun 2026) — PWA install nudge.
          Mounts above the footer in the chatting state only; not
          during onboarding (don't interrupt the setup flow) and not
          when chat is closed (install is moot after stay ends).
          Internal gates inside InstallNudge handle the "already
          installed", "previously dismissed", and "no install
          affordance available" cases — when any of those apply,
          the component renders nothing and there's zero layout
          impact.
        */}
        {!currentStepConfig && !chatClosed && (
          <InstallNudge personaName={persona.personaName} />
        )}
        <div
          className={`border-t border-moor-100 ${
            partnerPrimary ? 'bg-white' : 'bg-cream-50'
          }`}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="container-narrow">
            {currentStepConfig ? (
              currentStepConfig.mode === 'dates' ? (
                <OnboardingDateStep
                  key={currentStepConfig.id}
                  onSelect={handleQuickReply}
                />
              ) : currentStepConfig.mode === 'text' ? (
                <OnboardingTextStep
                  key={currentStepConfig.id}
                  step={currentStepConfig}
                  onSelect={handleQuickReply}
                />
              ) : (
                <QuickReplies
                  key={currentStepConfig.id}
                  step={currentStepConfig}
                  onSelect={handleQuickReply}
                />
              )
            ) : chatClosed ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-moor-600">
                  {t('chatClosed')}
                </p>
                <Link
                  to={`/memories/${user.townSlug}`}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-moor-700 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                >
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  {t('openKeepsake')}
                </Link>
              </div>
            ) : (
              <>
                {starterShown && starters.length > 0 && (
                  <StarterChips
                    starters={starters}
                    onPick={handleStarterPick}
                  />
                )}
                <ChatInput
                  onSend={handleSend}
                  disabled={isStreaming}
                  placeholder={t('messagePlaceholder', { personaName: persona.personaName })}
                  partnerPrimary={partnerPrimary}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
